import { FormField } from "@/components/elements/FormField";
import { Select } from "@/components/elements/Select";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { Grid } from "@mui/material";
import { FC, useCallback } from "react";
import { TCompanyForm } from "@/features/company/types/CompanyForm";
import { useFormContext, useWatch } from "react-hook-form";

export type TIndustryClassificationRowProps = {
  index: 1 | 2 | 3;
  control: ReturnType<typeof useFormContext<TCompanyForm>>["control"];
  readonly?: boolean;
  isLoadingIndustry: boolean;
  companySpecificIndustryOptions: Array<{ label: string; value: number; hidden?: boolean }>;
  getClassificationOptions: (id: number | string) => Array<{ label: string; value: number; hidden?: boolean }>;
  onIndustryChange: (newValue: number | null) => void;
};

export const IndustryClassificationRow: FC<TIndustryClassificationRowProps> = ({
  index,
  control,
  readonly,
  isLoadingIndustry,
  companySpecificIndustryOptions,
  getClassificationOptions,
  onIndustryChange,
}) => {
  const methods = useFormContext<TCompanyForm>();
  const industryFieldName = `companySpecificIndustryId${index}` as const;
  const classificationFieldName = `companySpecificBusinessClassificationIds${index}` as const;

  const industryId = useWatch({
    control,
    name: industryFieldName,
  });

  let classificationOptions = industryId ? getClassificationOptions(industryId) : [];

  if (classificationOptions?.length) {
    classificationOptions = classificationOptions.map((opt) => ({ ...opt, value: Number(opt.value) }));
  }

  const handleSelectChange = useCallback(
    (value: string) => {
      const numValue = value ? Number(value) : null;
      onIndustryChange(numValue);
    },
    [onIndustryChange],
  );

  const handleClassificationChange = useCallback(
    (values: string[]) => {
      const numValues = values.map((v) => Number(v));
      methods.setValue(classificationFieldName, numValues);
    },
    [methods, classificationFieldName],
  );

  return (
    <>
      <Grid item xs={6}>
        <FormField control={control} name={industryFieldName} label={`分野${index === 1 ? "①" : index === 2 ? "②" : "③"}`}>
          <Select
            loading={isLoadingIndustry}
            options={companySpecificIndustryOptions}
            placeholder="選択してください"
            disabled={readonly}
            onChange={handleSelectChange}
          />
        </FormField>
      </Grid>
      <Grid item xs={6}>
        <FormField control={control} name={classificationFieldName} label="区分">
          <SelectMulti
            options={classificationOptions}
            placeholder={industryId ? "選択してください" : "分野を先に選択してください"}
            disabled={readonly || !industryId}
            onChange={handleClassificationChange}
          />
        </FormField>
      </Grid>
    </>
  );
};
